import { Link, useParams } from '@tanstack/react-router';
import { ArrowRight, Award, Building2, ScanFace, Smartphone, Users } from 'lucide-react';
import SEO from 'app/javascript/components/shared/SEO';
import StoreBadges from 'app/javascript/components/shared/StoreBadges';
import { useAuth } from 'app/javascript/contexts/AuthContext';
import { ROUTES } from 'app/javascript/constants/routes';

const APP_FEATURES = [
  { icon: Award, text: 'Dados pessoais, faixas, graus e dependentes' },
  { icon: Users, text: 'Membros, solicitações e horários da academia' },
  { icon: ScanFace, text: 'Check-in por reconhecimento facial e cadastro do rosto' },
  { icon: Building2, text: 'Criação e configurações da equipe' },
];

// Landing for everything that left the web: profile settings, dependents, the
// academy admin, face enrollment and the creation wizard now live in the native
// app. Legacy `/app/*` URLs (e-mails, digests, bookmarks) all render this page.
export default function MovedToAppPage() {
  const { currentUser } = useAuth();
  const { _splat: legacyPath } = useParams({ strict: false }) as { _splat?: string };
  // Legacy academy URLs carry the slug (`settings/companies/:slug/…` and
  // `companies/:slug/face-enrollment`), so the visitor can still reach the
  // academy's public page from here.
  const companySlug = legacyPath?.match(/companies\/([^/]+)/)?.[1] ?? null;

  return (
    <main className="relative min-h-screen overflow-hidden bg-slate-950 px-4 py-12 text-white sm:px-6 md:py-20">
      <SEO
        title="BlackBelts | Agora no app"
        description="A edição do perfil e a administração da academia acontecem no app BlackBelts para iOS e Android."
      />

      <div className="pointer-events-none absolute inset-0" aria-hidden="true">
        <div className="absolute -left-24 top-12 h-72 w-72 rounded-full bg-yellow-400/10 blur-3xl" />
        <div className="absolute -right-24 bottom-0 h-96 w-96 rounded-full bg-yellow-400/5 blur-3xl" />
      </div>

      <section className="relative mx-auto w-full max-w-2xl">
        <div className="rounded-3xl border border-slate-800 bg-slate-900/80 p-6 shadow-2xl shadow-black/30 backdrop-blur sm:p-10">
          <p className="mb-3 flex items-center gap-2 text-sm font-bold uppercase tracking-[0.18em] text-yellow-400">
            <Smartphone className="h-4 w-4" aria-hidden="true" />
            Agora no app
          </p>
          <h1 className="text-balance font-bebas text-5xl leading-none tracking-tight sm:text-6xl">
            Essa área mudou para o app
          </h1>
          <p className="mt-5 max-w-lg text-base leading-relaxed text-slate-300">
            A edição do seu perfil e a administração da academia acontecem no BlackBelts para iOS e
            Android. O site continua com os perfis públicos de atletas e academias.
          </p>

          <ul className="mt-8 space-y-3">
            {APP_FEATURES.map(({ icon: Icon, text }) => (
              <li key={text} className="flex items-start gap-3 text-sm text-slate-300">
                <Icon className="mt-0.5 h-5 w-5 shrink-0 text-yellow-400" aria-hidden="true" />
                {text}
              </li>
            ))}
          </ul>

          <div className="my-8 flex items-center gap-3 text-xs font-bold uppercase tracking-[0.16em] text-slate-500">
            <span className="h-px flex-1 bg-slate-800" />
            Baixe grátis
            <span className="h-px flex-1 bg-slate-800" />
          </div>

          <StoreBadges className="justify-center" />
          <p className="mt-5 text-center text-sm leading-relaxed text-slate-400">
            Já tem o app? Abra o BlackBelts no celular e entre com a mesma conta.
          </p>

          <div className="mt-8 flex flex-col items-center gap-3 border-t border-slate-800 pt-6 sm:flex-row sm:justify-center sm:gap-6">
            {companySlug && (
              <Link
                to={ROUTES.COMPANY_DETAILS}
                params={{ slug: companySlug }}
                className="inline-flex items-center gap-2 text-sm font-semibold text-slate-300 transition-colors hover:text-yellow-400"
              >
                <Building2 className="h-4 w-4" aria-hidden="true" />
                Ver a página da academia
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
            )}
            {currentUser?.slug ? (
              <Link
                to={ROUTES.PUBLIC_PROFILE}
                params={{ slug: currentUser.slug }}
                className="inline-flex items-center gap-2 text-sm font-semibold text-slate-300 transition-colors hover:text-yellow-400"
              >
                Ver meu perfil público
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
            ) : (
              <Link
                to={ROUTES.HOME}
                reloadDocument
                className="inline-flex items-center gap-2 text-sm font-semibold text-slate-300 transition-colors hover:text-yellow-400"
              >
                Voltar ao início
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
            )}
          </div>
        </div>
      </section>
    </main>
  );
}
